import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/modules/auth/stores/authStore';

// Layouts
import MainLayout from '@/shared/layouts/MainLayout';
import AuthLayout from '@/shared/layouts/AuthLayout';

// Pages
import SplashPage from '@/pages/SplashPage';
import LoginPage from '@/modules/auth/pages/LoginPage';
import SignupPage from '@/modules/auth/pages/SignupPage';
import OAuthCallbackPage from '@/modules/auth/pages/OAuthCallbackPage';
import LobbyPage from '@/modules/lobby/pages/LobbyPage';
import SelectTopicsPage from '@/modules/lobby/pages/SelectTopicsPage';
import MatchingPage from '@/modules/lobby/pages/MatchingPage';
import DebateRoomPage from '@/modules/debate/pages/DebateRoomPage';
import ProfilePage from '@/modules/profile/pages/ProfilePage';
import EditProfilePage from '@/modules/profile/pages/EditProfilePage';
import NotificationSettingsPage from '@/modules/profile/pages/NotificationSettingsPage';
import TermsPage from '@/modules/profile/pages/TermsPage';
import PrivacyPolicyPage from '@/modules/profile/pages/PrivacyPolicyPage';
import AdminPage from '@/modules/admin/pages/AdminPage';

// AdSense Pages (Landing, About, FAQ, Guidelines)
import LandingPage from '@/modules/landing/pages/LandingPage';
import AboutPage from '@/modules/about/pages/AboutPage';
import FAQPage from '@/modules/faq/pages/FAQPage';
import GuidelinesPage from '@/modules/guidelines/pages/GuidelinesPage';

// Auth Guard - 로그인 사용자 또는 게스트 허용
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isGuest, isLoading } = useAuthStore();

  if (isLoading) {
    return <SplashPage />;
  }

  // 로그인 사용자 또는 게스트는 접근 허용
  if (!isAuthenticated && !isGuest) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// Root path: OAuth callback 처리 또는 랜딩 페이지 표시
function RootPage() {
  const searchParams = new URLSearchParams(window.location.search);
  const code = searchParams.get('code');
  
  // code가 있으면 OAuth 콜백 처리
  if (code) {
    return <OAuthCallbackPage />;
  }
  
  // 없으면 랜딩 페이지 표시
  return <LandingPage />;
}

function App() {
  return (
    <Routes>
      {/* Public Routes */}
      
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
      </Route>

      {/* OAuth Callback - 레이아웃 없이 */}
      <Route path="/oauth/callback" element={<OAuthCallbackPage />} />
      {/* Root path: OAuth callback 또는 랜딩 페이지 */}
      <Route path="/" element={<RootPage />} />

      {/* Protected Routes */}
      <Route
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/lobby" element={<LobbyPage />} />
        <Route path="/profile" element={<ProfilePage />} />
      </Route>

      {/* Full screen flows (no bottom nav) */}
      <Route
        path="/select-topics"
        element={
          <ProtectedRoute>
            <SelectTopicsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/matching"
        element={
          <ProtectedRoute>
            <MatchingPage />
          </ProtectedRoute>
        }
      />

      {/* Debate Room (별도 레이아웃) */}
      <Route
        path="/debate/:debateId"
        element={
          <ProtectedRoute>
            <DebateRoomPage />
          </ProtectedRoute>
        }
      />
      
      {/* Single Room (새로운 단일 게임방 시스템) */}
      <Route
        path="/room"
        element={
          <ProtectedRoute>
            <DebateRoomPage />
          </ProtectedRoute>
        }
      />

      {/* Admin (관리자 전용) */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminPage />
          </ProtectedRoute>
        }
      />

      {/* Profile Settings Pages */}
      <Route
        path="/profile/edit"
        element={
          <ProtectedRoute>
            <EditProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile/notifications"
        element={
          <ProtectedRoute>
            <NotificationSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />

      {/* AdSense Public Pages */}
      <Route path="/landing" element={<LandingPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/guidelines" element={<GuidelinesPage />} />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
